
import { defineComponent, PropType } from 'vue';
import FbxButton from '@/components/fbx-button.vue';
import FbxInput from '@/components/fbx-input.vue';
import VirtualRedTagHeader from '@/components/VirtualRedTag/VirtualRedTagHeader.vue';
import VirtualRedTagMethodIcon from '@/components/VirtualRedTag/VirtualRedTagMethodIcon.vue';
import queries from '@/queries';
import {
    isUnsupportedEmail,
    isValidEmail,
    isValidUsPhone,
    toE164UsPhone,
} from '@/shared/contactValidation';
import virtualRedTagNotificationFlow from '@/shared/mixins/virtualRedTagNotificationFlow';
import FbaApiError from '@/types/FbaApiError';
import {
    VirtualRedTagNotificationErrorKind,
    VirtualRedTagNotificationHttpStatus,
    VirtualRedTagNotificationChannel,
    VirtualRedTagNotificationRoute,
} from '@/types/VirtualRedTagNotification';

const NON_US_PHONE_HINT =
    'Only US phone numbers are supported. Please use email instead.';
const EMAIL_HINT =
    'Use an email address with letters, numbers, dots or hyphens, or choose Text';
const SAVE_FAILED_MESSAGE =
    "We couldn't save your notification preference. Please try again.";
const SESSION_EXPIRED_MESSAGE =
    'Your session has expired. Please reopen the app.';
// Rejections that retrying cannot fix. Each ends the form and offers the way out.
const RED_TAG_NOT_FOUND_MESSAGE =
    "We couldn't find this Red Tag for you. Please return to the start.";
const RED_TAG_TOO_OLD_MESSAGE =
    'This Red Tag is too old to add notifications. Please return to the start.';
const PREFERENCE_LOCKED_MESSAGE =
    "Notifications for this Red Tag can't be changed anymore. Please return to the start.";
const TERMINAL_GENERIC_MESSAGE =
    "We couldn't set up notifications for this Red Tag. Please return to the start.";
// The API's terminal 400 messages, lower-cased.
const TERMINAL_MESSAGES: Record<string, string> = {
    'red tag not found': RED_TAG_NOT_FOUND_MESSAGE,
    'trailer not found': RED_TAG_NOT_FOUND_MESSAGE,
    'assessment is not a virtual red tag': RED_TAG_NOT_FOUND_MESSAGE,
    'this red tag is too old to add notifications': RED_TAG_TOO_OLD_MESSAGE,
    'notification preference can no longer be changed':
        PREFERENCE_LOCKED_MESSAGE,
};
// Validation of the contact value is the only 400 the driver can fix by retyping.
const CONTACT_FIELD = 'contactvalue';
const MAX_SERVER_MESSAGE_LENGTH = 200;
// What apiErrorHandler puts in a FbaApiError when the server sent no message of its own.
const GENERIC_SERVER_MESSAGES = ['unknown error', 'no response from api'];

export default defineComponent({
    name: 'VirtualRedTagNotificationContact',
    components: {
        FbxButton,
        FbxInput,
        VirtualRedTagHeader,
        VirtualRedTagMethodIcon,
    },
    mixins: [virtualRedTagNotificationFlow],
    props: {
        // Arrives from the query string, so it can be anything at all.
        method: {
            type: String as PropType<string | undefined>,
            default: undefined,
        },
    },
    data: (): {
        contactValue: string;
        errorMessage: string;
        isSaving: boolean;
        isActive: boolean;
        errorKind: VirtualRedTagNotificationErrorKind | null;
        showNonUsPhoneHint: boolean;
        isClearingNonUsPhone: boolean;
    } => {
        return {
            contactValue: '',
            errorMessage: '',
            isSaving: false,
            isActive: true,
            errorKind: null,
            showNonUsPhoneHint: false,
            isClearingNonUsPhone: false,
        };
    },
    computed: {
        isDeadEnd(): boolean {
            return (
                this.errorKind === VirtualRedTagNotificationErrorKind.deadEnd
            );
        },
        // Only the two known values; anything else (missing, mistyped) is no method.
        notificationMethod(): VirtualRedTagNotificationChannel | undefined {
            return Object.values(VirtualRedTagNotificationChannel).find(
                (method) => method === this.method
            );
        },
        isEmail(): boolean {
            return (
                this.notificationMethod ===
                VirtualRedTagNotificationChannel.email
            );
        },
        title(): string {
            return this.isEmail ? 'Email Notifications' : 'Text Notifications';
        },
        inputLabel(): string {
            return this.isEmail ? 'Enter Email' : 'Enter Phone Number';
        },
        isContactValid(): boolean {
            return this.isEmail
                ? isValidEmail(this.contactValue)
                : isValidUsPhone(this.contactValue);
        },
        // Why the typed value cannot be used, before the driver ever presses Confirm.
        fieldHint(): string {
            if (this.isEmail) {
                return isUnsupportedEmail(this.contactValue) ? EMAIL_HINT : '';
            }
            return this.showNonUsPhoneHint ? NON_US_PHONE_HINT : '';
        },
        canConfirm(): boolean {
            return this.isContactValid && !this.isSaving && !this.isDeadEnd;
        },
    },
    watch: {
        // The route can change the method under a live instance; the typed value
        // belongs to the previous method.
        notificationMethod(): void {
            this.contactValue = '';
            this.errorMessage = '';
            this.errorKind = null;
            this.showNonUsPhoneHint = false;
            this.redirectIfNoMethod();
        },
    },
    created(): void {
        this.redirectIfNoMethod();
    },
    beforeUnmount(): void {
        // A save still in flight must not navigate once the driver has left.
        this.isActive = false;
    },
    methods: {
        // A missing or unknown method has nothing to enter; send the driver to pick one.
        // The mixin's created hook has already redirected if there is no assessment id.
        redirectIfNoMethod(): void {
            if (this.assessmentId && !this.notificationMethod) {
                this.$router.replace({
                    name: VirtualRedTagNotificationRoute.method,
                    params: { trailerNumber: this.trailerNumber },
                    query: this.notificationQuery(),
                });
            }
        },
        onContactChanged(value: string): void {
            if (!this.isEmail) {
                // fbx-input emits the raw typed or pasted text before it masks it, so
                // a paste has to be judged here, before the mask keeps 10 digits of it.
                const raw = (value ?? '').trim();
                const digits = raw.replace(/\D/g, '');

                // Our own clear of a rejected paste is still working its way through
                // the input (a garbled echo, then the empty value); ignore both.
                if (this.isClearingNonUsPhone) {
                    if (raw === '') {
                        this.isClearingNonUsPhone = false;
                    }
                    return;
                }

                // Not a US number: "+" followed by anything but 1, or too many digits.
                // Masking it would turn it into a different, US-looking number.
                const isNonUs =
                    (raw.startsWith('+') &&
                        digits.length > 0 &&
                        !digits.startsWith('1')) ||
                    digits.length > 11 ||
                    (digits.length === 11 && !digits.startsWith('1'));
                if (isNonUs) {
                    this.contactValue = '';
                    this.errorMessage = '';
                    this.showNonUsPhoneHint = true;
                    this.isClearingNonUsPhone = true;
                    this.$nextTick(() => this.setContactInput(''));
                    return;
                }

                // A US area code never starts with 1, so a leading 1 is the country
                // code: drop it and let the input re-mask (a pasted "+1 555 123 4567"
                // would otherwise lose its last digit).
                if (
                    (digits.length === 11 && digits.startsWith('1')) ||
                    digits === '1'
                ) {
                    this.contactValue = '';
                    this.errorMessage = '';
                    this.showNonUsPhoneHint = false;
                    this.$nextTick(() => this.setContactInput(digits.slice(1)));
                    return;
                }
            }
            this.showNonUsPhoneHint = false;
            this.contactValue = value ?? '';
            // Editing makes a failed save worth retrying; a dead end hides the input
            // and so never gets here.
            if (
                this.errorKind === VirtualRedTagNotificationErrorKind.retryable
            ) {
                this.errorKind = null;
            }
            this.errorMessage = '';
        },
        setContactInput(value: string): void {
            (
                this.$refs.contactInput as
                    { setValue(value: string): void } | undefined
            )?.setValue(value);
        },
        async onConfirm(): Promise<void> {
            // Bootstrap's .disabled only blocks pointer clicks; this also covers a
            // programmatic call and a second call while a save is in flight.
            if (!this.canConfirm || !this.notificationMethod) {
                return;
            }

            this.isSaving = true;
            this.errorMessage = '';
            this.errorKind = null;

            const result =
                await queries.postVirtualRedTagNotificationPreference({
                    assessmentId: this.assessmentId as string,
                    notificationMethod: this.notificationMethod,
                    contactValue: this.isEmail
                        ? this.contactValue.trim()
                        : toE164UsPhone(this.contactValue),
                });

            this.isSaving = false;

            // The driver left (or the route changed) while the save was in flight; a
            // late replace here would drag them to the confirmation.
            if (!this.isActive) {
                return;
            }

            if (result instanceof FbaApiError) {
                this.showError(result);
                return;
            }

            // Replace, so back from the confirmation does not land on a form that
            // would submit the same preference again.
            this.$router.replace({
                name: VirtualRedTagNotificationRoute.confirmed,
                params: { trailerNumber: this.trailerNumber },
                query: this.notificationQuery(
                    this.notificationMethod,
                    result.trailerAlreadyInShop
                ),
            });
        },
        showError(error: FbaApiError): void {
            const { kind, message } = this.classifyError(error);
            this.errorKind = kind;
            this.errorMessage = message;
        },
        classifyError(error: FbaApiError): {
            kind: VirtualRedTagNotificationErrorKind;
            message: string;
        } {
            const field = (error.field ?? '').toLowerCase();
            const serverMessage = (error.message ?? '').trim();
            const isBadRequest =
                error.statusCode ===
                VirtualRedTagNotificationHttpStatus.badRequest;

            if (
                error.statusCode ===
                VirtualRedTagNotificationHttpStatus.unauthorized
            ) {
                return {
                    kind: VirtualRedTagNotificationErrorKind.deadEnd,
                    message: SESSION_EXPIRED_MESSAGE,
                };
            }

            if (isBadRequest && field === CONTACT_FIELD) {
                // The API words contact validation for the driver (for example that
                // only US phone numbers are supported) and never echoes the contact
                // value, so it is shown as is. Stays retryable.
                const isUsable =
                    serverMessage.length > 0 &&
                    serverMessage.length <= MAX_SERVER_MESSAGE_LENGTH &&
                    !GENERIC_SERVER_MESSAGES.includes(
                        serverMessage.toLowerCase()
                    );
                return {
                    kind: VirtualRedTagNotificationErrorKind.retryable,
                    message: isUsable ? serverMessage : SAVE_FAILED_MESSAGE,
                };
            }

            // Any other named 400 is about the red tag itself; 403 and 404 are
            // harmless fallbacks, since the API reports these as 400s.
            if (
                (isBadRequest && field.length > 0) ||
                error.statusCode ===
                    VirtualRedTagNotificationHttpStatus.forbidden ||
                error.statusCode ===
                    VirtualRedTagNotificationHttpStatus.notFound
            ) {
                return {
                    kind: VirtualRedTagNotificationErrorKind.deadEnd,
                    message:
                        TERMINAL_MESSAGES[serverMessage.toLowerCase()] ??
                        (isBadRequest
                            ? TERMINAL_GENERIC_MESSAGE
                            : RED_TAG_NOT_FOUND_MESSAGE),
                };
            }

            return {
                kind: VirtualRedTagNotificationErrorKind.retryable,
                message: SAVE_FAILED_MESSAGE,
            };
        },
        navigateBack(): void {
            this.$router.push({
                name: VirtualRedTagNotificationRoute.method,
                params: { trailerNumber: this.trailerNumber },
                query: this.notificationQuery(),
            });
        },
    },
});
